import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Bot,
  CheckCircle,
  Loader2,
  Send,
  User,
  Plane,
  Car,
  Truck,
  PackageOpen,
  BarChart3,
  History,
  GitCompareArrows,
  Leaf,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  sendMessage,
  listThreads,
  getThreadMessages,
  deleteThread as deleteThreadApi,
} from '../services/shanthiService';
import AppLayout from '../components/layout/AppLayout';
import ToolCallCard from '../components/shanthi/ToolCallCard';
import ComparisonCard from '../components/shanthi/ComparisonCard';
import ThreadList from '../components/shanthi/ThreadList';

const THREAD_KEY = 'shanthi_thread_id';
const GREETING = {
  role: 'assistant',
  content:
    "Hi! I'm Shanthi, your Scope 3 carbon assistant.\n\nWhich would you like to calculate — upstream freight, downstream freight, business travel, or employee commute?",
};

// Quick-action chips shown above the input.
// `send: true` → auto-submit the prompt; otherwise prefill the textarea and focus it.
const QUICK_ACTIONS = [
  { label: 'Business travel',  icon: Plane,            prompt: "I'd like to calculate business travel emissions." },
  { label: 'Employee commute', icon: Car,              prompt: "I'd like to calculate employee commute emissions." },
  { label: 'Upstream freight', icon: Truck,            prompt: "I'd like to calculate upstream freight emissions." },
  { label: 'Downstream freight', icon: PackageOpen,    prompt: "I'd like to calculate downstream freight emissions." },
  { label: 'Compare modes',    icon: GitCompareArrows, prompt: 'Compare modes from ' },
  { label: 'Travel footprint', icon: Leaf,             prompt: "What's my business travel footprint?", send: true },
  { label: 'Freight footprint', icon: BarChart3,       prompt: "What's my freight footprint?", send: true },
  { label: 'My trips',         icon: History,          prompt: 'Show my recent trips', send: true },
  { label: 'My shipments',     icon: History,          prompt: 'Show my recent shipments', send: true },
];

function QuickActions({ onPick, disabled }) {
  return (
    <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2.5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
      {QUICK_ACTIONS.map((a) => {
        const Icon = a.icon;
        return (
          <button
            key={a.label}
            type="button"
            disabled={disabled}
            onClick={() => onPick(a)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon className="w-3.5 h-3.5" />
            {a.label}
          </button>
        );
      })}
    </div>
  );
}

function ActivityCard({ activity }) {
  const isShipment = !!activity.shipment_id || !!activity.flow;
  const title = isShipment
    ? activity.flow === 'upstream'
      ? 'Upstream shipment saved'
      : activity.flow === 'downstream'
      ? 'Downstream shipment saved'
      : 'Shipment saved'
    : activity.trip_type === 'employee_commute'
    ? 'Commute saved'
    : 'Trip saved';

  return (
    <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
      <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
        <CheckCircle className="w-4 h-4" />
        {title}
      </div>
      <div className="text-green-800 space-y-0.5">
        {activity.good && (
          <div><span className="font-medium">Goods:</span> {activity.good}</div>
        )}
        {activity.quantity != null && activity.unit && (
          <div>
            <span className="font-medium">Weight:</span>{' '}
            {activity.quantity} {activity.unit}
          </div>
        )}
        {activity.transport_mode && (
          <div><span className="font-medium">Mode:</span> {activity.transport_mode}</div>
        )}
        {!isShipment && activity.mode && (
          <div><span className="font-medium">Mode:</span> {activity.mode}</div>
        )}
        {activity.vehicle_type && (
          <div><span className="font-medium">Vehicle:</span> {activity.vehicle_type}</div>
        )}
        {(activity.origin || activity.destination) && (
          <div>
            <span className="font-medium">Route:</span>{' '}
            {[activity.origin, activity.destination].filter(Boolean).join(' → ')}
          </div>
        )}
        {activity.distance_km != null && (
          <div>
            <span className="font-medium">Distance:</span>{' '}
            {Number(activity.distance_km).toFixed(1)} km
          </div>
        )}
        {activity.cabin_class && (
          <div><span className="font-medium">Cabin:</span> {activity.cabin_class}</div>
        )}
        {activity.co2e_kg != null && (
          <div className="text-green-700 font-semibold mt-1">
            {Number(activity.co2e_kg).toFixed(2)} kg CO₂e
          </div>
        )}
      </div>
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-slate-400 italic">{msg.content}</span>
      </div>
    );
  }

  const steps = msg.tool_calls || msg.steps || [];
  // A compare_modes call produces a result dict with `comparisons` — pluck it
  // out so we can render a dedicated bar-chart card under the reply.
  const comparison = steps.find(
    (s) => s.name === 'compare_modes' && s.result && Array.isArray(s.result.comparisons)
  )?.result;

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-slate-700' : 'bg-emerald-600'
      }`}>
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      <div className={`max-w-[75%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        {!isUser && steps.length > 0 && (
          <div className="w-full space-y-1.5">
            {steps.map((s, i) => (
              <ToolCallCard
                key={i}
                step={s}
                defaultOpen={Array.isArray(s.substeps) && s.substeps.length > 0}
              />
            ))}
          </div>
        )}
        {msg.content && (
          <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
            isUser
              ? 'bg-slate-700 text-white rounded-tr-sm'
              : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
          }`}>
            {msg.content}
          </div>
        )}
        {!isUser && comparison && <ComparisonCard data={comparison} />}
        {msg.activity_saved && <ActivityCard activity={msg.activity_saved} />}
      </div>
    </div>
  );
}

export default function Shanthi() {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(() => localStorage.getItem(THREAD_KEY) || null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const refreshThreads = useCallback(async () => {
    try {
      setThreadsLoading(true);
      const data = await listThreads();
      setThreads(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silent — sidebar just stays empty, user can still chat.
      console.error('Failed to load threads:', err);
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  // On mount, load thread list. If we have a stored threadId, try to hydrate it;
  // otherwise just show the greeting.
  useEffect(() => {
    (async () => {
      await refreshThreads();
      const stored = localStorage.getItem(THREAD_KEY);
      if (stored) {
        try {
          setLoadingThread(true);
          const history = await getThreadMessages(stored);
          if (history && history.length > 0) {
            setMessages(history);
          } else {
            setMessages([GREETING]);
          }
        } catch {
          // Stale thread id — reset silently.
          localStorage.removeItem(THREAD_KEY);
          setThreadId(null);
          setMessages([GREETING]);
        } finally {
          setLoadingThread(false);
        }
      }
    })();
  }, [refreshThreads]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectThread = async (id) => {
    if (id === threadId) return;
    try {
      setLoadingThread(true);
      const history = await getThreadMessages(id);
      setThreadId(id);
      localStorage.setItem(THREAD_KEY, id);
      setMessages(history && history.length > 0 ? history : [GREETING]);
    } catch (err) {
      toast.error('Could not load that chat.');
    } finally {
      setLoadingThread(false);
    }
  };

  const handleDeleteThread = async (id) => {
    if (!window.confirm('Delete this chat? This cannot be undone.')) return;
    try {
      await deleteThreadApi(id);
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (id === threadId) {
        localStorage.removeItem(THREAD_KEY);
        setThreadId(null);
        setMessages([GREETING]);
      }
      toast.success('Chat deleted');
    } catch (err) {
      toast.error('Delete failed.');
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem(THREAD_KEY);
    setThreadId(null);
    setMessages([GREETING]);
    setInput('');
    textareaRef.current?.focus();
  };

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    setMessages((prev) => [
      ...prev,
      { role: 'system', content: 'Shanthi is thinking…' },
    ]);

    try {
      const data = await sendMessage(text, threadId);

      if (data.thread_id) {
        const isNew = data.thread_id !== threadId;
        setThreadId(data.thread_id);
        localStorage.setItem(THREAD_KEY, data.thread_id);
        if (isNew) refreshThreads();
      }

      setMessages((prev) => {
        const filtered = prev.filter((m) => m.content !== 'Shanthi is thinking…');
        return [
          ...filtered,
          {
            role: 'assistant',
            content: data.reply,
            tool_calls: data.steps || [],
            activity_saved: data.activity_saved || null,
          },
        ];
      });

      // Bump thread in the sidebar so last_message_at and title stay fresh.
      refreshThreads();

      if (data.activity_saved) {
        const saved = data.activity_saved;
        const successMsg = saved.shipment_id || saved.flow
          ? 'Shipment logged successfully!'
          : saved.trip_type === 'employee_commute'
          ? 'Commute logged successfully!'
          : 'Trip logged successfully!';
        toast.success(successMsg);
      }
    } catch (err) {
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.content !== 'Shanthi is thinking…');
        return [
          ...filtered,
          {
            role: 'assistant',
            content: err.message?.includes('401')
              ? 'Please log in again to continue.'
              : err.message?.includes('503') || err.message?.includes('unavailable')
              ? "I'm temporarily unavailable. Please try again in a moment."
              : `Sorry, something went wrong: ${err.message}`,
          },
        ];
      });
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (action) => {
    if (loading) return;
    if (action.send) {
      handleSend(action.prompt);
    } else {
      setInput(action.prompt);
      textareaRef.current?.focus();
      // Move cursor to end of prefilled prompt so user can continue typing.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) el.setSelectionRange(action.prompt.length, action.prompt.length);
      });
    }
  };

  return (
    <AppLayout
      title="Shanthi · Scope 3 Emissions Assistant"
      contentClassName="flex h-full p-0"
    >

      {/* Thread sidebar */}
      <div className="hidden md:flex">
        <ThreadList
          threads={threads}
          activeThreadId={threadId}
          loading={threadsLoading}
          onSelect={handleSelectThread}
          onDelete={handleDeleteThread}
          onNew={handleNewChat}
        />
      </div>

      {/* Chat panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 relative">
          {loadingThread && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            </div>
          )}
          <div className="max-w-2xl mx-auto space-y-4">
            {messages.map((msg, i) => (
              <Message key={msg.id || i} msg={msg} />
            ))}
            {loading && messages[messages.length - 1]?.role !== 'system' && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Input */}
        <div className="app-chrome border-t px-4 pt-3 pb-4 flex-shrink-0">
          <div className="max-w-2xl mx-auto">
            <QuickActions onPick={handleQuickAction} disabled={loading} />
          </div>
          <div className="max-w-2xl mx-auto flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe a trip, commute or shipment…"
              className="resize-none min-h-[44px] max-h-32 text-sm leading-6 py-2.5"
              rows={1}
              disabled={loading}
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              size="icon"
              className="flex-shrink-0 h-11 w-11"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-2">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
